import dayjs from 'dayjs';
import { DateDelimitersEnum, TimeDisplayFormat, TimeDisplayZone } from '../common/enums';
import { TimeSettings } from '../state/settings';

const dateSegments = [
    ['MM', 'DD', 'YYYY'],
    ['YYYY', 'MM', 'DD'],
    ['DD', 'MM', 'YYYY']
];

export type IDateTimeFormatter = Omit<
    _DateTimeFormatter,
    | 'dateFormat'
    | 'timeWithSecondsFormat'
    | 'timeWithoutSecondsFormat'
    | 'timeWithSecondsWithDayFormat'
    | 'timeWithoutSecondsWithDayFormat'
    | 'dateTimeFormat'
    | 'timeZone'
    | 'showDaysWithTime'
>;
export interface IDateTimeFormatter2 {
    formatTime(time: number | Date | null | undefined, type: 'hms' | 'hm' | 'hmd' | 'hmnd' | 'hh'): string;
    formatDate(time: number | null | undefined): string;
    formatDateTime(time: number | Date | null | undefined): string;
    formatDuration(time: number | null | undefined): string;
    formatDurationWithoutSeconds(time: number | null | undefined): string;
    formatTimeForPicker(date: Date | null, alwaysUTC?: boolean): Date | null;
    parseTimeFromPicker(date: Date | null, alwaysUTC?: boolean): Date | null;
}

class _DateTimeFormatter {
    private dateFormat = '';
    private timeWithSecondsFormat = '';
    private timeWithoutSecondsFormat = '';
    private timeWithSecondsWithDayFormat = '';
    private timeWithoutSecondsWithDayFormat = '';
    private dateTimeFormat = '';
    private timeZone: TimeDisplayZone = TimeDisplayZone.Utc;
    private showDaysWithTime: boolean = true;

    setProps(settings: TimeSettings) {
        this._buildFormatStrings(settings);
    }

    formatTime(time: number | Date | null | undefined, type: 'hms' | 'hm' | 'hmd' | 'hmnd' | 'hmsnd' | 'hh' = 'hm'): string {
        if (!time) {
            return '';
        }
        switch (type) {
            case 'hms':
                return this._toProperTimeZone(time).format(
                    this.showDaysWithTime ? this.timeWithSecondsWithDayFormat : this.timeWithSecondsFormat
                );
            case 'hm':
                return this._toProperTimeZone(time).format(
                    this.showDaysWithTime ? this.timeWithoutSecondsWithDayFormat : this.timeWithoutSecondsFormat
                );

            case 'hmd':
                return this._toProperTimeZone(time).format(this.timeWithoutSecondsWithDayFormat);

            case 'hmnd':
                return this._toProperTimeZone(time).format(this.timeWithoutSecondsFormat);

            case 'hh':
                return this._toProperTimeZone(time).format('hh');

            case 'hmsnd':
                return this._toProperTimeZone(time).format(this.timeWithSecondsFormat);

            default:
                return this._toProperTimeZone(time).format(this.timeWithSecondsWithDayFormat);
        }
    }

    formatDate(time: number | null | undefined): string {
        if (!time) {
            return '';
        }

        return this._toProperTimeZone(time).format(this.dateFormat);
    }

    formatDateTime(time: number | Date | null | undefined): string {
        if (!time) {
            return '';
        }

        return this._toProperTimeZone(time).format(this.dateTimeFormat);
    }

    formatDuration(time: number | null | undefined): string {
        if (time == null) {
            return '';
        }

        const h = Math.floor(time / 3600);
        time -= h * 3600;
        const m = Math.floor(time / 60);
        time -= m * 60;
        const s = time;
        return `${this.pad(h, 2)}:${this.pad(m, 2)}:${this.pad(s, 2)}`;
    }

    formatDurationWithoutSeconds(time: number | null | undefined): string {
        if (time == null) {
            return '';
        }
        const h = Math.floor(time / 3600);
        time -= h * 3600;
        const m = Math.floor(time / 60);
        return `${this.pad(h, 2)}:${this.pad(m, 2)}`;
    }

    formatTimeForPicker(date: Date | null, alwaysUTC?: boolean): Date | null {
        if (!date) {
            return null;
        }

        if (this.timeZone === TimeDisplayZone.Utc || alwaysUTC) {
            // For UTC display, create a date that shows UTC time as local time
            const utcTime = dayjs(date).utc();
            return new Date(utcTime.year(), utcTime.month(), utcTime.date(), utcTime.hour(), utcTime.minute(), utcTime.second());
        } else {
            // For local time display, use the date as-is
            return date;
        }
    }

    parseTimeFromPicker(date: Date | null, alwaysUTC?: boolean): Date | null {
        if (!date) {
            return null;
        }

        if (this.timeZone === TimeDisplayZone.Utc || alwaysUTC) {
            // Convert picker time back to UTC
            return dayjs()
                .utc()
                .hour(date.getHours())
                .minute(date.getMinutes())
                .second(date.getSeconds())
                .year(date.getFullYear())
                .month(date.getMonth())
                .day(date.getDay())
                .toDate();
        } else {
            // For local time, use as-is
            return date;
        }
    }

    getKendoTimeWithoutSecondsFormat() {
        return this.timeWithoutSecondsFormat.replace('[Z]', "'Z'");
    }

    getKendoDateTimeFormat() {
        return this.dateTimeFormat.replace('[Z]', "'Z'").replace('YYYY', 'y').replace('DD', 'd');
    }

    // Use padLeft/Right
    private pad(input: number, size: number) {
        let num = input.toString();
        while (num.length < size) {
            num = '0' + num;
        }
        return num;
    }

    private _toProperTimeZone(time: number | Date): dayjs.Dayjs {
        time = typeof time === 'number' ? time : (time as Date).getTime() / 1000;
        if (this.timeZone === TimeDisplayZone.Local) {
            return dayjs(time * 1000);
        }

        return dayjs.utc(time * 1000);
    }

    private _buildFormatStrings(settings: TimeSettings) {
        this.timeZone = settings.timeZone;
        this.showDaysWithTime = settings.showDaysWithTime;

        const delimiter = this._getDelimiter(settings);
        this.dateFormat = dateSegments[settings.dateFormat].join(delimiter);

        this.timeWithSecondsFormat =
            settings.timeFormat === TimeDisplayFormat.H24
                ? settings.timeZone === TimeDisplayZone.Local
                    ? 'HH:mm:ss'
                    : 'HH:mm:ss[Z]'
                : settings.timeZone === TimeDisplayZone.Local
                  ? 'hh:mm:ss A'
                  : 'hh:mm:ss[Z] A';

        this.timeWithoutSecondsFormat =
            settings.timeFormat === TimeDisplayFormat.H24
                ? settings.timeZone === TimeDisplayZone.Local
                    ? 'HH:mm'
                    : 'HH:mm[Z]'
                : settings.timeZone === TimeDisplayZone.Local
                  ? 'hh:mm A'
                  : 'hh:mm[Z] A';

        this.timeWithSecondsWithDayFormat = this.timeWithSecondsFormat + '/DD';
        this.timeWithoutSecondsWithDayFormat = this.timeWithoutSecondsFormat + '/DD';

        this.dateTimeFormat = `${this.dateFormat} ${this.timeWithSecondsFormat}`;
    }

    private _getDelimiter(settings: TimeSettings) {
        switch (settings.dateDelimiter) {
            case DateDelimitersEnum.Slash:
                return '/';
            case DateDelimitersEnum.Dot:
                return '.';
            case DateDelimitersEnum.Dash:
                return '-';
        }
    }
}

export const DateTimeFormatter: IDateTimeFormatter = new _DateTimeFormatter();
